import type { DispatchCoreKind } from "@storyboard/dispatch-view/fieldKinds";
import { CORE_FIELD_KINDS, DISPATCH_CORE_KINDS } from "@storyboard/dispatch-view/fieldKinds";
import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { ProjectedRecord } from "@storyboard/driver-query/predicate";
import { coreFieldRef, fieldKey, tagFieldRef } from "@storyboard/driver-query/predicate";
import type { DriverRow } from "@/types/driver";
import { locationText } from "@/utils/locationText";
import type { DispatchItem } from "./dispatchItems";

/** The record key of a Dispatch core field, e.g. `core:name`. */
export function dispatchCoreKey(kind: DispatchCoreKind): string {
  return fieldKey(coreFieldRef(kind));
}

/** The record key of a tag column, e.g. `tag:region`. */
export function dispatchTagKey(slug: string): string {
  return fieldKey(tagFieldRef(slug));
}

function booleanValue(value: boolean | null | undefined): string {
  return value ? "true" : "false";
}

/**
 * A task's three fields under the given kinds: its pinned view id, its creator, and its instant; empty without one.
 *
 * The view id and the membership id are what sort and group; the page labels them at render.
 */
export function taskFields(
  task: OpenTask | null | undefined,
  view: DispatchCoreKind,
  author: DispatchCoreKind,
  at: DispatchCoreKind,
): ProjectedRecord {
  return {
    [dispatchCoreKey(view)]: task?.pin?.viewId ?? "",
    [dispatchCoreKey(author)]: task?.task.createdBy ?? "",
    [dispatchCoreKey(at)]: task?.task.createdAt ?? "",
  };
}

/**
 * Flattens a driver row to the `field key → value` record the Dispatch view's filters, search, and sort evaluate.
 *
 * Core kinds project to their `DriverRow` strings (`unread`, `hands_free`, `personal_device` to `"true"`/`"false"`,
 * `availability` to `"neverLoggedIn"` in place of the calculated value for a driver who reads as never logged in,
 * `volume` to the raw number as a string, `latest_item_at` to the ISO time of the driver's latest message or newer
 * required TMS event, `location` to the text the location surfaces show (`locationText`), `coordinates` to the
 * device fix as `"latitude,longitude"` or empty without one, `pta` and `next_stop` to the current route's planned time
 * of availability and next stop name, or empty on no route); `unacknowledged` is the channel's acknowledgement state,
 * which lives outside `DriverRow`; `task_view`, `task_author`, and `task_at` are the open task's pinned view id (empty
 * while unpinned), its creator's membership id, and its instant, all empty without a task. The message kinds describe one message and are empty on a driver row; `alert` and
 * `unhandled_count` are reserved and project empty until later projects populate them. Each tag slug in `tagIdBySlug`
 * projects to the driver's value list for that tag, so predicates match per value.
 *
 * @param tagIdBySlug - The workspace's enabled tags, slug → id; every slug becomes a `tag:<slug>` key.
 * @param unacknowledged - Whether any driver message on the channel still waits on an acknowledgement.
 * @param task - The open task the row carries, if any.
 */
export function projectDispatchRecord(
  row: DriverRow,
  tagIdBySlug: ReadonlyMap<string, string>,
  unacknowledged = false,
  task?: OpenTask,
): ProjectedRecord {
  const record: ProjectedRecord = {
    [dispatchCoreKey("name")]: row.name ?? "",
    [dispatchCoreKey("alias")]: row.alias ?? "",
    [dispatchCoreKey("group")]: row.group ?? "",
    [dispatchCoreKey("recent_message")]: row.recentMessage ?? "",
    [dispatchCoreKey("latest_item_at")]: row.latestItemCreatedAt ?? "",
    [dispatchCoreKey("unread")]: booleanValue(row.unread),
    [dispatchCoreKey("unacknowledged")]: booleanValue(unacknowledged),
    [dispatchCoreKey("duty_status")]: row.mostRecentDutyStatusStatus ?? "",
    [dispatchCoreKey("driving_status")]: row.mostRecentDrivingStatus ?? "",
    [dispatchCoreKey("availability")]: row.neverLoggedIn ? "neverLoggedIn" : (row.calculatedAvailability ?? ""),
    [dispatchCoreKey("location")]: locationText(row)?.text ?? "",
    [dispatchCoreKey("coordinates")]:
      row.latitude != null && row.longitude != null ? `${row.latitude},${row.longitude}` : "",
    [dispatchCoreKey("state_province")]: row.stateProvince ?? "",
    [dispatchCoreKey("country")]: row.country ?? "",
    [dispatchCoreKey("volume")]: row.volume == null ? "" : String(row.volume),
    [dispatchCoreKey("hands_free")]: booleanValue(row.mostRecentHandsFreeStatusEnabled),
    [dispatchCoreKey("personal_device")]: booleanValue(row.hasPersonalDevice),
    [dispatchCoreKey("pta")]: row.route?.pta ?? "",
    [dispatchCoreKey("next_stop")]: row.route?.nextStop?.name ?? "",
    ...taskFields(task, "task_view", "task_author", "task_at"),
    [dispatchCoreKey("alert")]: "",
    [dispatchCoreKey("unhandled_count")]: "",
  };
  // Every message kind is empty on a driver row, so a message-grain view's fields always resolve to a key.
  for (const kind of DISPATCH_CORE_KINDS) {
    if (CORE_FIELD_KINDS[kind].entity === "message") record[dispatchCoreKey(kind)] = "";
  }
  for (const [slug, tagId] of tagIdBySlug) {
    record[dispatchTagKey(slug)] = row.tags[tagId] ?? [];
  }
  return record;
}

/** The Kind a Dispatch item projects as: a task pinned to another board is a task, as a shelf task row is. */
const ITEM_KIND_BY_SOURCE: Record<DispatchItem["source"], string> = {
  message: "message",
  tmsEvent: "event",
  otherBoardTask: "task",
};

/**
 * The record of a message row: the driver's projection (`base`, from {@link projectDispatchRecord}, shared by every
 * message on the channel) with the message kinds filled from the item. A message or event item is the driver's, so
 * `item_sender` is the driver's name; a task pinned to another board is a dispatcher's, so its `item_sender` is empty
 * and its task line names the author.
 *
 * Every driver kind the registry points at a message twin (`recent_message`, `latest_item_at`, `unread`,
 * `unacknowledged`) is re-projected from the message itself: on a message row the driver-level kinds mean the row,
 * so a view laid out for drivers (a Time and a Message column, an "Unacknowledged only" toggle) reads correctly when a
 * member switches the grain to messages, each row showing its own message and time rather than the driver's latest.
 */
export function projectDispatchItemRecord(
  base: ProjectedRecord,
  driver: DriverRow,
  item: DispatchItem,
): ProjectedRecord {
  const record: ProjectedRecord = { ...base };
  record[dispatchCoreKey("item_text")] = item.text ?? "";
  record[dispatchCoreKey("item_at")] = item.createdAt;
  // A task pinned to another board is the dispatcher's, so it names no sender; its author is in its task fields.
  record[dispatchCoreKey("item_sender")] = item.source === "otherBoardTask" ? "" : (driver.name ?? driver.alias ?? "");
  record[dispatchCoreKey("item_unread")] = booleanValue(item.unread);
  record[dispatchCoreKey("item_unacknowledged")] = booleanValue(!item.acknowledged);
  record[dispatchCoreKey("item_kind")] = ITEM_KIND_BY_SOURCE[item.source];
  Object.assign(record, taskFields(item.task, "item_task_view", "item_task_author", "item_task_at"));
  for (const kind of DISPATCH_CORE_KINDS) {
    const twin = CORE_FIELD_KINDS[kind].atMessageGrain;
    if (twin) record[dispatchCoreKey(kind)] = record[dispatchCoreKey(twin)] ?? "";
  }
  return record;
}
